import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { collection, doc, getDoc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../AuthContext";
import { useActiveBar } from "../ActiveBarContext";
import { loadBarWeek } from "../shared/barStats";
import SignupLinkCard from "../shared/SignupLinkCard.jsx";

// The owner's home screen: the three things they do most, what customers
// see for their bar today, and the staff posting code.

const DAYS_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Today's date in Minnesota time, "YYYY-MM-DD" (matches the Cloud Function).
function todayCentral() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function todayWeekdayIndex() {
  const name = new Intl.DateTimeFormat("en-US", { timeZone: "America/Chicago", weekday: "long" }).format(new Date());
  return DAYS_FULL.indexOf(name);
}

function formatTime(time24) {
  if (!time24) return "";
  const [h, m] = time24.split(":");
  const hour = parseInt(h, 10);
  if (Number.isNaN(hour)) return time24;
  const period = hour >= 12 ? "pm" : "am";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return m === "00" ? `${hour12}${period}` : `${hour12}:${m}${period}`;
}

function scheduleLabel(item) {
  if (item.recurrence === "daily") return "Every day";
  if (item.recurrence === "weekly") return `Every ${item.dayOfWeek}`;
  if (item.recurrence === "days") return (item.days || []).join(", ");
  if (item.recurrence === "one-time") return "Today only";
  return "";
}

const card = {
  background: "#17131F",
  borderRadius: "14px",
  padding: "14px 16px",
};

export default function Today() {
  const { myBars, activeBar } = useActiveBar();
  const { refreshUserBars } = useAuth();
  const [events, setEvents] = useState([]);
  const [foodPost, setFoodPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [week, setWeek] = useState(null);

  useEffect(() => {
    setWeek(null);
    if (activeBar) loadBarWeek(activeBar.id).then(setWeek).catch(() => setWeek(null));
  }, [activeBar]);

  const today = todayCentral();
  const dayIdx = todayWeekdayIndex();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!activeBar) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const snap = await getDocs(query(collection(db, "events"), where("barId", "==", activeBar.id)));
        const post = await getDoc(doc(db, "todaysSpecials", `${activeBar.id}_${today}`));
        if (cancelled) return;
        setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setFoodPost(post.exists() ? post.data() : null);
      } catch (e) {
        console.error("Today page load error:", e);
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [activeBar, today]);

  if (myBars.length === 0) {
    return <Navigate to="/owner/claim-bar" replace />;
  }

  const todaysEvents = events
    .filter((e) => {
      if (e.recurrence === "daily") return true;
      if (e.recurrence === "weekly") return e.dayOfWeek === DAYS_FULL[dayIdx];
      if (e.recurrence === "one-time") return e.date === today;
      return false;
    })
    .sort((a, b) => (a.time || "").localeCompare(b.time || ""));

  const todaysSpecials = (activeBar?.specials || []).filter((s) => {
    if (s.recurrence === "daily") return true;
    if (s.recurrence === "days") return (s.days || []).includes(DAYS_SHORT[dayIdx]);
    if (s.recurrence === "one-time") return s.date === today;
    return false;
  });

  const generateStaffCode = async () => {
    if (!activeBar) return;
    if (activeBar.staffCode && !window.confirm("Make a new code? The old one will stop working right away.")) return;
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    await updateDoc(doc(db, "bars", activeBar.id), { staffCode: code });
    await refreshUserBars();
  };

  const prettyDate = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(new Date());

  const actionBase = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "22px",
    borderRadius: "16px",
    textDecoration: "none",
  };

  return (
    <div style={{ maxWidth: "1040px", display: "flex", flexDirection: "column", gap: "28px" }}>
      <div>
        <h1 style={{ margin: 0, fontFamily: "'Bebas Neue', Inter, sans-serif", fontWeight: 400, fontSize: "44px", letterSpacing: "0.5px" }}>
          Today at {activeBar?.name || "your bar"}
        </h1>
        <p style={{ margin: "4px 0 0", fontSize: "16px", color: "#ACA8BF" }}>{prettyDate}</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
        <Link to="/owner/todays-special" style={{ ...actionBase, background: "#FF3D81", color: "#0B0B12" }}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
            <circle cx="12" cy="13.5" r="3.5" />
          </svg>
          <span style={{ fontSize: "20px", fontWeight: 700 }}>Post today's food special</span>
          <span style={{ fontSize: "15px" }}>Snap a photo. Live until midnight.</span>
        </Link>
        <Link to="/owner/post-update" style={{ ...actionBase, background: "#17131F", border: "1px solid #3A3350", color: "#fff" }}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#B9A6FF" strokeWidth="2" aria-hidden="true">
            <path d="M4 5h16v11H8l-4 4z" />
          </svg>
          <span style={{ fontSize: "20px", fontWeight: 700 }}>Send a quick update</span>
          <span style={{ fontSize: "15px", color: "#ACA8BF" }}>"Bingo starts at 6!" to people nearby.</span>
        </Link>
        <Link to="/owner/create-event" style={{ ...actionBase, background: "#17131F", border: "1px solid #3A3350", color: "#fff" }}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#B9A6FF" strokeWidth="2" aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
          </svg>
          <span style={{ fontSize: "20px", fontWeight: 700 }}>Add an event or special</span>
          <span style={{ fontSize: "15px", color: "#ACA8BF" }}>Set it once. It repeats for you.</span>
        </Link>
      </div>

      <SignupLinkCard bar={activeBar} />

      <section style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <h2 style={{ margin: 0, fontSize: "20px" }}>Your last 7 days on Bar Fun Finder</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "12px" }}>
          {[
            { n: week?.seen, label: "people saw your bar" },
            { n: week?.opened, label: "opened your page" },
            { n: week ? week.calls + week.directions + week.website : undefined, label: "called, got directions or visited your site" },
            { n: week?.saves, label: "saved your bar" },
            { n: week?.offersUsed, label: "used your offers" },
          ].map((c) => (
            <div key={c.label} style={{ ...card, padding: "18px", display: "flex", flexDirection: "column", gap: "4px" }}>
              <div style={{ fontSize: "32px", fontWeight: 700 }}>{c.n ?? "–"}</div>
              <div style={{ fontSize: "15px", color: "#ACA8BF" }}>{c.label}</div>
            </div>
          ))}
        </div>
      </section>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "flex-start" }}>
        <section style={{ flex: "2 1 380px", minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
          <h2 style={{ margin: 0, fontSize: "20px" }}>Showing to customers today</h2>

          {loading ? (
            <p style={{ color: "#ACA8BF", margin: 0 }}>Loading…</p>
          ) : (
            <>
              {foodPost ? (
                <div style={{ ...card, display: "flex", gap: "12px", alignItems: "center" }}>
                  <img src={foodPost.photoUrl} alt="" style={{ width: "64px", height: "64px", objectFit: "cover", borderRadius: "10px" }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "16px", fontWeight: 600 }}>{foodPost.caption}</div>
                    <div style={{ fontSize: "14px", color: "#ACA8BF" }}>Food photo · until midnight</div>
                  </div>
                  <Link to="/owner/todays-special" style={{ fontSize: "15px", color: "#FF6FA3" }}>Replace</Link>
                </div>
              ) : (
                <div style={{ ...card, background: "transparent", border: "1px dashed #5A5070", display: "flex", gap: "12px", alignItems: "center" }}>
                  <div style={{ flex: 1, fontSize: "15px", color: "#ACA8BF" }}>No food photo posted yet today</div>
                  <Link to="/owner/todays-special" style={{ fontSize: "15px", fontWeight: 600, color: "#FF6FA3" }}>Post one</Link>
                </div>
              )}

              {todaysEvents.map((ev) => (
                <div key={ev.id} style={{ ...card, display: "flex", gap: "12px", alignItems: "center" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "16px", fontWeight: 600 }}>
                      {ev.title}{ev.time ? ` · ${formatTime(ev.time)}` : ""}
                    </div>
                    <div style={{ fontSize: "14px", color: "#ACA8BF" }}>{scheduleLabel(ev)}</div>
                  </div>
                  <Link to="/owner/create-event" style={{ fontSize: "15px", color: "#FF6FA3" }}>Edit</Link>
                </div>
              ))}

              {todaysSpecials.map((s) => (
                <div key={s.id || s.title} style={{ ...card, display: "flex", gap: "12px", alignItems: "center" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "16px", fontWeight: 600 }}>
                      {s.title}
                      {s.startTime && s.endTime ? ` · ${formatTime(s.startTime)}–${formatTime(s.endTime)}` : ""}
                    </div>
                    <div style={{ fontSize: "14px", color: "#ACA8BF" }}>Special · {scheduleLabel(s)}</div>
                  </div>
                  <Link to="/owner/specials" style={{ fontSize: "15px", color: "#FF6FA3" }}>Edit</Link>
                </div>
              ))}

              {todaysEvents.length === 0 && todaysSpecials.length === 0 && (
                <p style={{ margin: 0, fontSize: "15px", color: "#ACA8BF" }}>
                  No events or specials scheduled for today.{" "}
                  <Link to="/owner/create-event" style={{ color: "#FF6FA3" }}>Add one</Link>
                </p>
              )}
            </>
          )}
        </section>

        <section style={{ ...card, flex: "1 1 260px", minWidth: 0, padding: "18px", display: "flex", flexDirection: "column", gap: "10px" }}>
          <h2 style={{ margin: 0, fontSize: "18px" }}>Staff posting code</h2>
          <div style={{ padding: "12px", borderRadius: "10px", background: "#0B0B12", fontFamily: "monospace", fontSize: "26px", letterSpacing: "4px", textAlign: "center" }}>
            {activeBar?.staffCode || "—"}
          </div>
          <p style={{ margin: 0, fontSize: "15px", color: "#ACA8BF" }}>
            Your bartender or cook opens <strong style={{ color: "#fff" }}>portal.barfunfinder.com/staff-post</strong> on
            their phone and enters this code to post food photos. No login needed.
          </p>
          <button
            type="button"
            onClick={generateStaffCode}
            style={{ minHeight: "44px", borderRadius: "10px", border: "1px solid #3A3350", background: "transparent", color: "#fff", font: "600 15px Inter, sans-serif", cursor: "pointer" }}
          >
            {activeBar?.staffCode ? "Make a new code" : "Create a code"}
          </button>
        </section>
      </div>
    </div>
  );
}
